import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { createOrganization, updateOrganization } from '@/lib/api/endpoints/organizations';
import { mergeFieldErrors, trimToNull, type LocalFieldErrors } from '@/lib/form-errors';
import { slugify } from './hierarchy';
import {
  ORGANIZATION_STATUSES,
  type Organization,
  type OrganizationStatus,
} from '@/lib/api/types';

/*
  Create or edit an organization, meaning a customer company.

  One dialog for both, because the fields are the same except for status, which only exists
  once the record does. Two dialogs would be two copies of the slug rules.

  The slug is the field worth explaining. It is unique per tenant, it appears in URLs and in
  the enrollment package a customer's technician runs, and changing it later invalidates
  anything that referenced the old one. So it is suggested from the name while the name is being
  typed, stops following the name the moment it is edited by hand, and on an existing
  organization comes with a warning rather than being silently editable.

  Validation is in three layers and each catches something the others cannot:

    on submit, locally      empty name, empty or malformed slug. No round trip needed.
    from the server, 422    anything the client's copy of the rules missed
    from the server, 409    the slug is already taken, which only the server can know
    from the server, 403    a reserved slug, which needs platform.manage

  The 409 and the 403 land as field errors or as a summary alert, never as a silent close. The
  dialog stays open with the typed values intact.
*/

export interface OrganizationFormDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Null to create, an organization to edit. */
  readonly organization: Organization | null;
}

const STATUS_HELP: Readonly<Record<OrganizationStatus, string>> = {
  active: 'Normal. Devices connect, sessions can be started.',
  suspended: 'Kept but paused. Use this while a billing or contract question is open.',
  archived: 'Closed customer. Retained for the audit trail.',
};

export function OrganizationFormDialog({
  open,
  onOpenChange,
  organization,
}: OrganizationFormDialogProps): React.JSX.Element {
  const formId = `org-form-${useId()}`;
  const queryClient = useQueryClient();
  const editing = organization !== null;

  const [name, setName] = useState(organization?.name ?? '');
  const [slug, setSlug] = useState(organization?.slug ?? '');
  const [slugEdited, setSlugEdited] = useState(editing);
  const [externalRef, setExternalRef] = useState(organization?.externalRef ?? '');
  const [status, setStatus] = useState<OrganizationStatus>(organization?.status ?? 'active');
  const [local, setLocal] = useState<LocalFieldErrors>({});

  // Reopening loads the record again rather than keeping a cancelled edit. Render-phase
  // adjustment, not an effect: see the note in ConfirmDialog.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setName(organization?.name ?? '');
      setSlug(organization?.slug ?? '');
      setSlugEdited(editing);
      setExternalRef(organization?.externalRef ?? '');
      setStatus(organization?.status ?? 'active');
      setLocal({});
    }
  }

  const mutation = useMutation({
    mutationFn: async (): Promise<Organization> => {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        externalRef: trimToNull(externalRef),
      };
      if (organization === null) {
        return createOrganization(payload);
      }
      return updateOrganization(organization.id, { ...payload, status });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      onOpenChange(false);
    },
  });

  const errors = mergeFieldErrors(local, mutation.error);

  /**
   * Clears the previous failure as soon as a field is edited.
   *
   * Without this the form shows a stale rejection: type a duplicate slug, get told it is taken,
   * change it, and the old message sits under the new value claiming the new slug is the one
   * that clashed. The local errors go with it, because they were produced by the same submit.
   */
  function clearSubmitError(): void {
    if (mutation.isError) {
      mutation.reset();
    }
    if (Object.keys(local).length > 0) {
      setLocal({});
    }
  }

  function validate(): LocalFieldErrors {
    const found: Record<string, string> = {};
    if (name.trim() === '') {
      found.name = 'A name is required. This is what technicians see in every list.';
    }
    const candidate = slug.trim();
    if (candidate === '') {
      found.slug = 'A slug is required.';
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(candidate)) {
      found.slug =
        'Lower case letters, digits and single hyphens between them, for example acme-dental.';
    }
    return found;
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const found = validate();
    setLocal(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Edit ${organization.name}` : 'New organization'}
      description={
        editing
          ? 'An organization is a customer company. Every device, site and group belongs to exactly one.'
          : 'An organization is a customer company. Create it before minting an enrollment token, because a device cannot exist without one.'
      }
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Create organization'}
          </Button>
        </>
      }
    >
      {/* A real form, so Enter in any of these single-line fields submits it. */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field
          label="Name"
          required
          autoFocus
          value={name}
          maxLength={120}
          autoComplete="off"
          hint="The customer's name as your technicians say it."
          onChange={(event) => {
            const next = event.target.value;
            clearSubmitError();
            setName(next);
            if (!slugEdited) {
              setSlug(slugify(next));
            }
          }}
          {...(errors.forField('name') === undefined ? {} : { error: errors.forField('name') })}
        />

        <Field
          label="Slug"
          required
          value={slug}
          maxLength={64}
          autoComplete="off"
          className="font-mono"
          hint={
            editing
              ? 'Changing this breaks any link or enrollment package that used the old slug. Unique within the tenant.'
              : 'Used in URLs and in the enrollment package. Unique within the tenant, and awkward to change later.'
          }
          onChange={(event) => {
            clearSubmitError();
            setSlugEdited(true);
            setSlug(event.target.value.toLowerCase());
          }}
          {...(errors.forField('slug') === undefined ? {} : { error: errors.forField('slug') })}
        />

        <Field
          label="PSA reference"
          value={externalRef}
          maxLength={80}
          autoComplete="off"
          hint="Optional. The customer's id in your PSA or billing system, so the two can be reconciled."
          onChange={(event) => {
            clearSubmitError();
            setExternalRef(event.target.value);
          }}
          {...(errors.forField('externalRef') === undefined
            ? {}
            : { error: errors.forField('externalRef') })}
        />

        {editing ? (
          <div>
            <Select
              label="Status"
              value={status}
              options={ORGANIZATION_STATUSES.map((candidate) => ({
                value: candidate,
                label: candidate,
              }))}
              onValueChange={(next) => {
                setStatus(next as OrganizationStatus);
              }}
            />
            <p className="mt-1.5 text-xs text-fg-subtle">{STATUS_HELP[status]}</p>
          </div>
        ) : null}

        {/*
          Shown when the failure is not already explained under a field: a duplicate slug that
          came back without a field key, a reserved slug refused for permissions, a transport
          failure. Never nothing.
        */}
        {mutation.isError && !errors.allErrorsAreFieldLevel ? (
          <ApiErrorAlert
            error={mutation.error}
            context={editing ? 'Could not save the organization' : 'Could not create the organization'}
          />
        ) : null}
      </form>
    </Dialog>
  );
}
